/* eslint-disable no-param-reassign */
import { getElementRect } from '@src/framework/editor/src/page/index/components/canvas/element-wrapper/utils';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { Container, ImageFill, InteractionEvent, InteractionEventHandler, SvgGraphics } from '@tencent/alloy-renderer';
import { imageCache } from '@tencent/alloy-renderer-adapter';
import { IDisposable } from '@tencent/alloy-renderer/dist/lib/event-emitter';
import ua from '@tencent/docs-user-agent';
import _debounce from 'lodash/debounce';
import _isEqual from 'lodash/isEqual';
import { CANVAS_WORD_ART_GRAPHICS_ID } from '../../../id-constants';
import { InterfaceFactoryOptions } from '../../elements/factory-options.interface';
import { InterfaceRendererShapeComponent } from '../../elements/types';
import { TextEditor } from '../text-editor';
import { Area, CharStructListLite, PrstTxWarp, WordArtRender, WordArtRenderOptions, WordArtRenderProps } from './word-art-render/type';
import { RenderExtraContext } from './word-art-type/common.interface';

// 挂载 wordArt 渲染器
let wordArtRender: WordArtRender | null = null;

export class WordArtGraphics extends Container {
    // 图形构造入参
    private factoryOptions: InterfaceFactoryOptions<IShapeModel>;
    // 基础 graphics 部分
    private parentComponent: InterfaceRendererShapeComponent;
    // graphics
    private graphics: SvgGraphics | null;

    private clickDisposer?: IDisposable;
    private contextMenuDisposer?: IDisposable;

    private prstTxWarp: PrstTxWarp;
    private renderExtraContext: RenderExtraContext;
    private wordArtRenderResultPick: {
        contentArea: Area;
        charStructListLite: CharStructListLite;
    };

    private callRenderProps?: WordArtRenderProps;
    private callRenderOptions?: WordArtRenderOptions;
    private oldCallRenderProps?: WordArtRenderProps;

    public constructor(
        factoryOptions: InterfaceFactoryOptions<IShapeModel>,
        parentComponent: InterfaceRendererShapeComponent,
    ) {
        super();
        this.id = CANVAS_WORD_ART_GRAPHICS_ID;
        this.factoryOptions = factoryOptions;
        this.parentComponent = parentComponent;
        this.bindEvent();
    }

    public dispose = () => {
        super.dispose();
        this.clickDisposer?.dispose();
        this.contextMenuDisposer?.dispose();
        eventBus.off(EventKeys.WORD_ART_RENDER, this.wordArtRenderCallback);
    };

    public update = (context: RenderExtraContext) => {
        const { model } = this.factoryOptions;

        this.prstTxWarp = model.textBodyStyle?.bodyStyle?.prstTxWarp as PrstTxWarp;
        this.renderExtraContext = context;

        const callRenderProps = {
            renderQuality: 1,
            curScreenWidth: window.screen.width * window.devicePixelRatio,
            curScreenHeight: window.screen.height * window.devicePixelRatio,
            prstTxWarp: this.prstTxWarp,
            ...this.renderExtraContext,
        };

        const callRenderOptions = {
            useWorker: !!global.Worker,
        };

        this.callRenderProps = callRenderProps;
        this.callRenderOptions = callRenderOptions;

        if (this.graphics) {
            if (_isEqual(this.oldCallRenderProps, callRenderProps)) {
                this.updateGraphics();
            } else {
                this.callWordArtRenderWhenGraphicsDidMount(callRenderProps, callRenderOptions);
            }
        } else {
            this.callWordArtRenderWhenGraphicsWillMount(callRenderProps, callRenderOptions);
        }

        this.oldCallRenderProps = callRenderProps;
    };

    private updateGraphics = () => {
        if (!this.graphics) return;
        const rect = getElementRect({ element: this.factoryOptions.model, tableResizing: false, useAdvHas: false });
        const { width, height } = rect;
        this.graphics.width = width;
        this.graphics.height = height;
    };

    private callWordArtRenderWhenGraphicsWillMount = (
        callRenderProps: WordArtRenderProps,
        callRenderOptions: WordArtRenderOptions,
    ) => {
        if (wordArtRender) {
            callRenderProps.renderQuality = 1 / 20;
            wordArtRender.callRender(callRenderProps, callRenderOptions);
        } else {
            this.getWordArtRender().then((module) => {
                callRenderProps.renderQuality = 1 / 20;
                module.callRender(callRenderProps, callRenderOptions);
            });
        }
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private callWordArtRenderWhenGraphicsDidMount = _debounce(
        (callRenderProps, callRenderOptions) => {
            if (this.factoryOptions.region === 'list') {
                callRenderProps.renderQuality = 1 / 20;
            } else {
                callRenderProps.renderQuality = 1;
            }
            wordArtRender?.callRender(callRenderProps, callRenderOptions);
        },
        100,
        {
            leading: true,
            trailing: true,
        },
    );

    // 绑定事件
    private bindEvent = () => {
        if (!ua.isMobile) {
            this.clickDisposer = this.onClick(this.clickCallback);
            this.contextMenuDisposer = this.onContextMenu(this.contextMenuCallback);
        }
        eventBus.on(EventKeys.WORD_ART_RENDER, this.wordArtRenderCallback);
    };

    // 点击回调
    private clickCallback: InteractionEventHandler = (ev: InteractionEvent) => {
        const textEditor = (this.parentComponent as any).textEditor as TextEditor | null;

        if (textEditor?.readonly) return;

        const textDirection = this.factoryOptions.model.textBodyStyle?.wrapperStyle?.textDirection
            ?? TextDirectionType.HORIZONTAL;

        const { contentArea } = this.wordArtRenderResultPick;
        const { rectTextList } = this.renderExtraContext;

        if (this.parent && textEditor) {
            const { editor } = textEditor;
            const { worldX, worldY } = ev;
            const { minX, minY, maxX, maxY } = this.worldBounds;
            const xRatio = (worldX - minX) / (maxX - minX);
            const yRatio = (worldY - minY) / (maxY - minY);

            const x = (xRatio * contentArea.width) + contentArea.offset.x;
            const y = (yRatio * contentArea.height) + contentArea.offset.y;

            let currentRectTextId = 0;
            let offset = 0;

            this.wordArtRenderResultPick.charStructListLite.forEach((charStruct) => {
                if (charStruct.bounds && charStruct.center) {
                    const { minX, minY, maxX, maxY } = charStruct.bounds;
                    if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
                        if (textDirection === TextDirectionType.HORIZONTAL) {
                            if (x >= charStruct.center.x) {
                                offset = 1;
                            }
                        } else {
                            if (y >= charStruct.center.y) {
                                offset = 1;
                            }
                        }
                        currentRectTextId = charStruct.id;
                    }
                }
            });

            const filteredRectTextList = rectTextList.filter(rectText => rectText.content.length === 1);
            const currentRectText = rectTextList[currentRectTextId];
            let index = 0;
            let currentYIndex = 0;
            let currentXIndex = 0;

            // eslint-disable-next-line no-restricted-syntax
            for (const iterator of (editor.slatePathToBox as any).map.keys()) {
                const [yIndex, xIndex] = iterator.split('-').map(Number);
                if (filteredRectTextList[index] === currentRectText) {
                    currentYIndex = yIndex;
                    currentXIndex = xIndex;
                }
                index += 1;
            }

            editor.focus();
            editor.setSelection({
                anchor: {
                    offset,
                    path: [currentYIndex, currentXIndex],
                },
                focus: {
                    offset,
                    path: [currentYIndex, currentXIndex],
                },
            });
            this.factoryOptions.selectionService?.selectedElements.add(this.factoryOptions.model);
        }
    };

    // contextMenu 回调
    private contextMenuCallback = () => {
        this.factoryOptions.selectionService?.selectedElements.add(this.factoryOptions.model);
    };

    private wordArtRenderCallback = ({ key, src, contentArea, charStructListLite }) => {
        if (this.parent?.id !== key) return;
        this.wordArtRenderResultPick = { contentArea, charStructListLite };
        this.renderSvgGraphics(src);
    };

    private renderSvgGraphics = (src: string) => {
        const rect = getElementRect({ element: this.factoryOptions.model, tableResizing: false, useAdvHas: false });
        const { width, height } = rect;
        const path = `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`;
        const image = imageCache.get({
            alloyParams: { urlOrImage: src },
        });

        if (!this.graphics) {
            this.graphics = new SvgGraphics(path);
            this.graphics.x = 0;
            this.graphics.y = 0;
            this.graphics.fill = new ImageFill(image);
            this.addChild(this.graphics);

            // 这里需要重新调用一次，除了 list 区域，其他区域需要按照 renderQuality 为 1 重新渲染一次
            if (this.factoryOptions.region !== 'list') {
                if (this.callRenderProps && this.callRenderOptions) {
                    this.callRenderProps.renderQuality = 1;
                    wordArtRender?.callRender(this.callRenderProps, this.callRenderOptions);
                }
            }
        } else {
            this.graphics.path = path;
            this.graphics.fill = new ImageFill(image);
        }
    };

    // 异步获取 wordArt 渲染器
    private getWordArtRender = () =>
        import('./word-art-render').then((module) => {
            wordArtRender = module;
            return module;
        });
}
